
import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { CATALOG_STALE_BASE_MS, staleTimeWithJitterFromKey } from "@/lib/query-cache";
import { queryKeys } from "@/lib/query-keys";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { 
  Search, 
  Dumbbell, 
  BookOpen, 
  Trophy,
  ArrowRight,
  Target,
  Zap,
  User,
  Clipboard
} from "lucide-react";
import { ClientPageLayout } from "@/components/client/ClientPageLayout";
import ProfileIconLink from "@/components/ProfileIconLink";
import WorkoutTemplatesView from "./explore/WorkoutTemplatesView";
import KnowledgeHubView from "./explore/KnowledgeHubView";
import EquipmentStoreView from "./explore/EquipmentStoreView";
import AssessmentsView from "./explore/AssessmentsView";
import ExercisesView from "./explore/ExercisesView";
import { useEnforceLegalApproval } from "@/hooks/use-enforce-legal";
import { listTemplates } from "@/integrations/backend/workout-templates";
import { listLessons } from "@/integrations/backend/lessons";
import { listEquipment } from "@/integrations/backend/equipment";
import { listClientAssessments } from "@/integrations/backend/assessments";
import { listExercises } from "@/integrations/backend/exercises";

type ExploreCategory = "overview" | "workouts" | "knowledge" | "equipment" | "assessments" | "exercises";

const ExploreHub = () => {
  useEnforceLegalApproval();
  const [activeCategory, setActiveCategory] = useState<ExploreCategory>("overview");
  const [searchQuery, setSearchQuery] = useState("");
  const [workoutCount, setWorkoutCount] = useState<number | null>(null);
  const [lessonCount, setLessonCount] = useState<number | null>(null);
  const [equipmentCount, setEquipmentCount] = useState<number | null>(null);
  const [assessmentCount, setAssessmentCount] = useState<number | null>(null);
  const [exerciseCount, setExerciseCount] = useState<number | null>(null);
  // Deep-link support: allow navigating directly to a category via pathname, hash, or query
  React.useEffect(() => {
    try {
      const { pathname, hash, search } = window.location;
      const params = new URLSearchParams(search);
      const tab = (params.get("tab") || params.get("category") || (hash || "").replace("#", "") || "").toLowerCase();
      if (pathname === "/assessments" || tab === "assessments") {
        setActiveCategory("assessments");
        return;
      }
      if (pathname === "/workout-library" || tab === "workouts" || tab === "workout" || tab === "templates") {
        setActiveCategory("workouts");
        return;
      }
    } catch (e) {
      console.warn("ExploreHub deep-link parsing failed", e);
    }
  }, []);

  const templatesCountQ = useQuery({
    queryKey: queryKeys.workoutTemplates.list("All"),
    queryFn: () => listTemplates(),
    staleTime: staleTimeWithJitterFromKey(CATALOG_STALE_BASE_MS, [...queryKeys.workoutTemplates.list("All")]),
  });
  const lessonsCountQ = useQuery({
    queryKey: queryKeys.lessons.list(false),
    queryFn: () => listLessons(false),
    staleTime: staleTimeWithJitterFromKey(CATALOG_STALE_BASE_MS, [...queryKeys.lessons.list(false)]),
  });
  const equipmentCountQ = useQuery({
    queryKey: queryKeys.equipment.all,
    queryFn: listEquipment,
    staleTime: staleTimeWithJitterFromKey(CATALOG_STALE_BASE_MS, [...queryKeys.equipment.all]),
  });
  const assessmentsCountQ = useQuery({
    queryKey: queryKeys.assessments.clientList,
    queryFn: listClientAssessments,
    staleTime: staleTimeWithJitterFromKey(CATALOG_STALE_BASE_MS, [...queryKeys.assessments.clientList]),
  });
  const exercisesCountQ = useQuery({
    queryKey: queryKeys.exercises.clientList,
    queryFn: listExercises,
    staleTime: staleTimeWithJitterFromKey(CATALOG_STALE_BASE_MS, [...queryKeys.exercises.clientList]),
  });

  useEffect(() => {
    if (templatesCountQ.data) setWorkoutCount(templatesCountQ.data.length);
  }, [templatesCountQ.data]);
  useEffect(() => {
    if (lessonsCountQ.data) setLessonCount(lessonsCountQ.data.length);
  }, [lessonsCountQ.data]);
  useEffect(() => {
    if (equipmentCountQ.data) setEquipmentCount(equipmentCountQ.data.length);
  }, [equipmentCountQ.data]);
  useEffect(() => {
    if (assessmentsCountQ.data) setAssessmentCount(assessmentsCountQ.data.length);
  }, [assessmentsCountQ.data]);
  useEffect(() => {
    if (exercisesCountQ.data) setExerciseCount(exercisesCountQ.data.length);
  }, [exercisesCountQ.data]);

  const categories = [
    {
      id: "workouts" as const,
      title: "Workout Templates",
      description: "Discover and start new workouts designed for your goals",
      icon: Dumbbell,
      color: "from-emerald-500 to-emerald-600",
      stats: workoutCount !== null ? `${workoutCount} template${workoutCount !== 1 ? 's' : ''}` : "Loading..."
    },
    {
      id: "knowledge" as const,
      title: "Learn & Knowledge",
      description: "Expand your fitness knowledge with expert content",
      icon: BookOpen,
      color: "from-blue-500 to-blue-600",
      stats: lessonCount !== null ? `${lessonCount} lesson${lessonCount !== 1 ? "s" : ""}` : "Loading..."
    },
    {
      id: "equipment" as const,
      title: "Explore Equipment",
      description: "Find the perfect gear for your fitness journey",
      icon: Dumbbell,
      color: "from-orange-500 to-orange-600",
      stats: equipmentCount !== null ? `${equipmentCount} item${equipmentCount !== 1 ? "s" : ""}` : "Loading..."
    },
    {
      id: "assessments" as const,
      title: "Assessments",
      description: "Track your progress and complete fitness evaluations",
      icon: Clipboard,
      color: "from-purple-500 to-purple-600",
      stats: assessmentCount !== null ? `${assessmentCount} assessment${assessmentCount !== 1 ? "s" : ""}` : "Loading..."
    },
    {
      id: "exercises" as const,
      title: "Exercises",
      description: "Browse and discover exercises with smart filters",
      icon: Dumbbell,
      color: "from-pink-500 to-pink-600",
      stats: exerciseCount !== null ? `${exerciseCount} exercise${exerciseCount !== 1 ? "s" : ""}` : "Loading..."
    }
  ];

  const handleCategorySelect = (categoryId: ExploreCategory) => {
    setActiveCategory(categoryId);
  };

  if (activeCategory !== "overview") {
    return (
      <ClientPageLayout activeTab="explore" contentClassName="p-0 sm:p-0 space-y-0">
        {activeCategory === "workouts" && (
          <WorkoutTemplatesView onBack={() => setActiveCategory("overview")} />
        )}
        {activeCategory === "knowledge" && (
          <KnowledgeHubView onBack={() => setActiveCategory("overview")} />
        )}
        {activeCategory === "equipment" && (
          <EquipmentStoreView onBack={() => setActiveCategory("overview")} />
        )}
        {activeCategory === "assessments" && (
          <AssessmentsView onBack={() => setActiveCategory("overview")} />
        )}
        {activeCategory === "exercises" && (
          <ExercisesView onBack={() => setActiveCategory("overview")} initialSearch={searchQuery} />
        )}
      </ClientPageLayout>
    );
  }

  return (
    <ClientPageLayout
      activeTab="explore"
      header={
        <>
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Target className="w-6 h-6" />
              <span>Explore</span>
            </h1>
            <ProfileIconLink />
          </div>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-light w-5 h-5" />
            <Input
              placeholder="Search workouts, lessons, equipment, exercises..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-dark-card border border-primary/10 text-white placeholder:text-gray-light"
            />
          </div>
        </>
      }
    >
        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card
            className="border border-primary/10 shadow-card bg-dark-card cursor-pointer transition-all hover:ring-1 hover:ring-primary/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            role="button"
            tabIndex={0}
            aria-label="Explore workouts"
            onClick={() => handleCategorySelect("workouts")}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") handleCategorySelect("workouts");
            }}
          >
            <CardContent className="p-3 text-center text-white">
              <div className="w-8 h-8 bg-dark-slate rounded-full flex items-center justify-center mx-auto mb-1">
                <Dumbbell className="w-4 h-4 text-gray-light" />
              </div>
              <div className="text-base font-bold leading-tight">
                {workoutCount !== null ? workoutCount : "..."}
              </div>
              <div className="text-[11px] text-gray-light">Workouts</div>
            </CardContent>
          </Card>
          
          <Card
            className="border border-primary/10 shadow-card bg-dark-card cursor-pointer transition-all hover:ring-1 hover:ring-primary/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            role="button"
            tabIndex={0}
            aria-label="Explore learning and knowledge"
            onClick={() => handleCategorySelect("knowledge")}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") handleCategorySelect("knowledge");
            }}
          >
            <CardContent className="p-3 text-center text-white">
              <div className="w-8 h-8 bg-dark-slate rounded-full flex items-center justify-center mx-auto mb-1">
                <BookOpen className="w-4 h-4 text-gray-light" />
              </div>
              <div className="text-base font-bold leading-tight">{lessonCount !== null ? lessonCount : "..."}</div>
              <div className="text-[11px] text-gray-light">Lessons</div>
            </CardContent>
          </Card>
          
          <Card
            className="border border-primary/10 shadow-card bg-dark-card cursor-pointer transition-all hover:ring-1 hover:ring-primary/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            role="button"
            tabIndex={0}
            aria-label="Explore equipment"
            onClick={() => handleCategorySelect("equipment")}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") handleCategorySelect("equipment");
            }}
          >
            <CardContent className="p-3 text-center text-white">
              <div className="w-8 h-8 bg-dark-slate rounded-full flex items-center justify-center mx-auto mb-1">
                <Dumbbell className="w-4 h-4 text-gray-light" />
              </div>
              <div className="text-base font-bold leading-tight">{equipmentCount !== null ? equipmentCount : "..."}</div>
              <div className="text-[11px] text-gray-light">Equipment</div>
            </CardContent>
          </Card>
          
          <Card
            className="border border-primary/10 shadow-card bg-dark-card cursor-pointer transition-all hover:ring-1 hover:ring-primary/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            role="button"
            tabIndex={0}
            aria-label="Explore assessments"
            onClick={() => handleCategorySelect("assessments")}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") handleCategorySelect("assessments");
            }}
          >
            <CardContent className="p-3 text-center text-white">
              <div className="w-8 h-8 bg-dark-slate rounded-full flex items-center justify-center mx-auto mb-1">
                <Clipboard className="w-4 h-4 text-gray-light" />
              </div>
              <div className="text-base font-bold leading-tight">{assessmentCount !== null ? assessmentCount : "..."}</div>
              <div className="text-[11px] text-gray-light">Assessments</div>
            </CardContent>
          </Card>

          <Card
            className="border border-primary/10 shadow-card bg-dark-card cursor-pointer transition-all hover:ring-1 hover:ring-primary/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            role="button"
            tabIndex={0}
            aria-label="Explore exercises"
            onClick={() => handleCategorySelect("exercises")}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") handleCategorySelect("exercises");
            }}
          >
            <CardContent className="p-3 text-center text-white">
              <div className="w-8 h-8 bg-dark-slate rounded-full flex items-center justify-center mx-auto mb-1">
                <Dumbbell className="w-4 h-4 text-gray-light" />
              </div>
              <div className="text-base font-bold leading-tight">{exerciseCount !== null ? exerciseCount : "..."}</div>
              <div className="text-[11px] text-gray-light">Exercises</div>
            </CardContent>
          </Card>
        </div>

        {/* Category Cards */}
        <div>
          <h2 className="text-xl font-bold mb-4">What would you like to explore?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {categories.map((category) => (
              <Card 
                key={category.id}
                className="border border-primary/10 bg-dark-card shadow-card overflow-hidden cursor-pointer transition-all group hover:ring-1 hover:ring-primary/20"
                onClick={() => handleCategorySelect(category.id)}
              >
                <div className="p-6 text-white relative">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        category.id === "workouts" ? "bg-strength text-white" :
                        category.id === "knowledge" ? "bg-conditioning text-white" :
                        category.id === "equipment" ? "bg-mobility text-black" :
                        "bg-cardio text-white"
                      }`}>
                        <category.icon className="w-5 h-5" />
                      </div>
                      <h3 className="text-xl font-bold">{category.title}</h3>
                    </div>
                    <ArrowRight className="w-5 h-5 text-gray-light group-hover:translate-x-1 transition-transform" />
                  </div>
                  <p className="text-gray-light text-sm mb-3">{category.description}</p>
                  <div className="text-xs bg-dark-slate rounded-full px-3 py-1 inline-block text-gray-light">
                    {category.stats}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
    </ClientPageLayout>
  );
};

export default ExploreHub;
